import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Link2 } from "lucide-react";
import { API } from "@/config";

/**
 * PromoCodeSection — customer applies a Firefly Wishes / admin promo code or
 * affiliate referral code. Auto-applies referral codes stored by ReferralTracker.
 */
export const PromoCodeSection = ({ reservationId, email, quote, setQuote }) => {
  const [partnerMe, setPartnerMe] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState(
    quote?.promo_code ? { code: quote.promo_code, email } : null
  );
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [referralBanner, setReferralBanner] = useState(null);

  useEffect(() => {
    axios.get(`${API}/partners/me`).then((r) => setPartnerMe(r.data.partner)).catch(() => setPartnerMe(null));
  }, []);

  // Auto-apply referral code from sessionStorage if no promo already applied
  useEffect(() => {
    if (!reservationId || quote?.promo_code || quote?.partner_id) return;
    try {
      const raw = sessionStorage.getItem("_egr_ref");
      if (!raw) return;
      const ref = JSON.parse(raw);
      if (!ref?.code || Date.now() - ref.stored_at > 86400000) return; // 24h TTL
      setReferralBanner(ref);
      setPromoInput(ref.code);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("[PromoCodeSection] sessionStorage read failed:", e);
    }
  }, [reservationId, quote?.promo_code, quote?.partner_id]);

  const applyPromoCode = async (codeOverride) => {
    const code = (codeOverride || promoInput || "").trim();
    if (!code) { setPromoError("Enter a code"); return; }
    setPromoBusy(true); setPromoError("");
    try {
      await axios.post(`${API}/checkout/update`, {
        reservation_id: reservationId,
        customer_email: email || undefined,
        promo_code: code.toUpperCase(),
      });
      const r = await axios.post(`${API}/checkout/update`, { reservation_id: reservationId });
      setQuote(r.data.quote);
      setPromoApplied({ code: code.toUpperCase(), email });
      setPromoInput(code.toUpperCase());
      toast.success("Code applied ✨");
    } catch (e) {
      setPromoApplied(null);
      setPromoError(e.response?.data?.detail || "That code isn't valid.");
    } finally { setPromoBusy(false); }
  };

  const applyReferral = () => {
    if (referralBanner?.code) applyPromoCode(referralBanner.code);
  };

  const clearPromoCode = async () => {
    setPromoApplied(null);
    setPromoInput("");
    setPromoError("");
    setReferralBanner(null);
    try {
      const r = await axios.post(`${API}/checkout/update`, { reservation_id: reservationId, promo_code: "" });
      setQuote(r.data.quote);
    } catch (e) { console.warn("clearPromoCode failed:", e?.message); }
  };

  if (partnerMe && quote?.partner_id) {
    return (
      <div className="glass p-5 sm:p-6 border border-[#FFD166]/40" data-testid="partner-booking-banner">
        <p className="eyebrow text-[#FFD166] mb-1">Booking as {partnerMe.business_name}</p>
        <p className="text-sm text-[#F6F1E4]">This booking is attributed to your partner account automatically{Number(quote.partner_discount) > 0 ? ` — partner discount applied to rental items` : ""}. No code needed.</p>
      </div>
    );
  }
  return (
    <div className="glass p-5 sm:p-6" data-testid="promo-section">
      {referralBanner && !promoApplied && !quote?.partner_id && (
        <div className="rounded-lg border border-[#FFD166]/30 bg-[#FFD166]/5 p-3 mb-3 flex items-center justify-between gap-2"
          data-testid="referral-banner">
          <div className="flex items-center gap-2 text-sm min-w-0">
            <Link2 className="w-4 h-4 text-[#FFD166] shrink-0" />
            <span className="text-[#F6F1E4]">
              Referral offer from <b className="text-[#FFD166]">{referralBanner.partner_name}</b>:{" "}
              {referralBanner.discount_description}
            </span>
          </div>
          <button type="button" onClick={applyReferral} disabled={promoBusy}
            className="btn-ghost shrink-0 text-xs" data-testid="referral-apply-btn">
            {promoBusy ? "Applying…" : "Apply"}
          </button>
        </div>
      )}
      {!promoApplied ? (
        <>
          <label className="eyebrow flex items-center gap-2 mb-2 text-[#FFD166]">
            <Sparkles className="w-3.5 h-3.5" /> Promo / Firefly Wishes code
          </label>
          <div className="flex gap-2">
            <input
              className="input flex-1 tracking-wider uppercase"
              type="text"
              placeholder="Enter your code"
              value={promoInput}
              onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromoCode(); } }}
              data-testid="promo-input"
              autoComplete="off"
              spellCheck={false}
            />
            <button type="button" onClick={() => applyPromoCode()} disabled={promoBusy} className="btn-ghost shrink-0 disabled:opacity-60" data-testid="promo-apply-btn">
              {promoBusy ? "Checking…" : "Apply"}
            </button>
          </div>
          {promoError && <p className="text-[11px] text-red-300 mt-2" data-testid="promo-error">{promoError}</p>}
          <p className="text-[10px] text-[#B9C8D5]/70 mt-1.5">Have a promo or a Firefly Wishes code we sent you? Drop it in.</p>
        </>
      ) : (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-3 flex items-center justify-between gap-3" data-testid="promo-applied">
          <div className="flex items-center gap-2 text-emerald-300 text-sm min-w-0">
            <Sparkles className="w-4 h-4 shrink-0 text-[#FFD166]" />
            <span className="truncate"><b className="text-[#FFD166]">{promoApplied.code}</b> applied</span>
          </div>
          <button type="button" onClick={clearPromoCode} className="text-[#B9C8D5] hover:text-[#FFD166] text-xs shrink-0" data-testid="promo-remove-btn">Remove</button>
        </div>
      )}
    </div>
  );
};
